import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Users, Brain, CheckSquare, TrendingUp, Shield, ArrowUpRight, ArrowDownRight, Activity, Target } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import TrainingWidget from '../components/TrainingWidget';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Brand colors
const BRAND_COLORS = {
  primary: '#1e3a5f',
  teal: '#0d9488',
  light: '#e8f4fc',
  orange: '#f59e0b',
  red: '#ef4444',
  green: '#10b981',
  blue: '#3b82f6'
};

const Dashboard = () => {
  const { user, loading: authLoading, company } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    // Don't fetch if auth is still loading or user not authenticated
    if (authLoading || !user) {
      return;
    }
    
    try {
      setError(null);
      setLoading(true);
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Only show error if we're authenticated - 401 means auth issue, not data issue
      if (error?.response?.status === 401) {
        setError('Sessão expirada. Por favor, faça login novamente.');
      } else {
        setError(error?.response?.data?.detail || 'Erro ao carregar dados do dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [authLoading, user]);

  useEffect(() => {
    // Only fetch stats when auth is complete and user exists
    if (!authLoading && user) {
      fetchStats();
    }
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchStats();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, [authLoading, user, fetchStats]);

  // Show loading while auth is initializing or while fetching stats
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#e8f4fc] rounded-full"></div>
            <div className="w-16 h-16 border-4 border-[#0d9488] border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <span className="text-[#64748b] font-medium">
            {authLoading ? 'Verificando sessão...' : 'Carregando dados...'}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4 text-center p-6">
          <AlertTriangle className="h-12 w-12 text-[#f59e0b]" />
          <h3 className="text-lg font-semibold text-[#1e3a5f]">Erro ao carregar dashboard</h3>
          <p className="text-[#64748b]">{error}</p>
          <button 
            onClick={() => { setLoading(true); fetchStats(); }}
            className="px-4 py-2 bg-[#0d9488] text-white rounded-lg hover:bg-[#0d9488]/90 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4 text-center p-6">
          <Activity className="h-12 w-12 text-[#64748b]" />
          <h3 className="text-lg font-semibold text-[#1e3a5f]">Nenhum dado disponível</h3>
          <p className="text-[#64748b]">Comece cadastrando funcionários e riscos para visualizar as estatísticas.</p>
        </div>
      </div>
    );
  }

  const riskColors = {
    baixo: BRAND_COLORS.green,
    medio: BRAND_COLORS.orange,
    alto: '#F97316',
    critico: BRAND_COLORS.red
  };

  const riskChartData = [
    { name: 'Baixo', value: stats?.risk_distribution?.baixo || 0, color: riskColors.baixo },
    { name: 'Médio', value: stats?.risk_distribution?.medio || 0, color: riskColors.medio },
    { name: 'Alto', value: stats?.risk_distribution?.alto || 0, color: riskColors.alto },
    { name: 'Crítico', value: stats?.risk_distribution?.critico || 0, color: riskColors.critico }
  ];

  const riskTypeData = Object.entries(stats?.risk_by_type || {}).map(([key, value]) => ({
    name: key,
    value
  }));

  // Função para calcular trend real baseado em dados históricos
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return null; // Sem histórico
    const change = ((current - previous) / previous) * 100;
    return {
      value: change >= 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`,
      isUp: change >= 0
    };
  };

  const kpiCards = [
    {
      title: 'Total de Riscos',
      value: stats?.total_risks || 0,
      icon: AlertTriangle,
      trend: stats?.total_risks > 0 ? calculateTrend(stats?.total_risks, stats?.previous_total_risks) : null,
      gradient: 'from-orange-500 to-amber-400',
      bgLight: 'bg-gradient-to-br from-orange-50 to-amber-50'
    },
    {
      title: 'Riscos Críticos',
      value: stats?.high_risks || 0,
      icon: Shield,
      trend: stats?.high_risks > 0 ? calculateTrend(stats?.high_risks, stats?.previous_high_risks) : null,
      gradient: 'from-red-500 to-rose-400',
      bgLight: 'bg-gradient-to-br from-red-50 to-rose-50'
    },
    {
      title: 'Avaliações Realizadas',
      value: stats?.total_assessments || 0,
      icon: Brain,
      trend: stats?.total_assessments > 0 ? calculateTrend(stats?.total_assessments, stats?.previous_assessments) : null,
      gradient: 'from-[#0d9488] to-teal-400',
      bgLight: 'bg-gradient-to-br from-teal-50 to-cyan-50'
    },
    {
      title: 'Ações Pendentes',
      value: stats?.pending_actions || 0,
      icon: CheckSquare,
      trend: stats?.pending_actions > 0 ? calculateTrend(stats?.pending_actions, stats?.previous_pending_actions) : null,
      gradient: 'from-blue-500 to-indigo-400',
      bgLight: 'bg-gradient-to-br from-blue-50 to-indigo-50'
    },
    {
      title: 'Total Funcionários',
      value: stats?.total_employees || 0,
      icon: Users,
      trend: stats?.total_employees > 0 ? calculateTrend(stats?.total_employees, stats?.previous_employees) : null,
      gradient: 'from-emerald-500 to-green-400',
      bgLight: 'bg-gradient-to-br from-emerald-50 to-green-50'
    },
    {
      title: 'Índice Psicossocial',
      value: stats?.avg_psychosocial_index || 0,
      icon: Activity,
      suffix: '/5.0',
      trend: stats?.avg_psychosocial_index > 0 ? calculateTrend(stats?.avg_psychosocial_index, stats?.previous_psychosocial_index) : null,
      gradient: 'from-[#1e3a5f] to-[#0d9488]',
      bgLight: 'bg-gradient-to-br from-slate-50 to-teal-50'
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Banner */}
      <div 
        className="relative overflow-hidden rounded-2xl p-8"
        style={{ 
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0d9488 100%)'
        }}
      >
        {/* Decorative Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <circle cx="5" cy="5" r="1" fill="white"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)"/>
          </svg>
        </div>
        
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl md:text-3xl font-bold text-white mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Bem-vindo ao CERP Brasil NR1
            </h1>
            <p className="text-white/80 max-w-xl text-sm md:text-base">
              Acompanhe os indicadores de saúde e segurança no trabalho da sua empresa em tempo real.
            </p>
          </div>
          {/* Decorative Shield */}
          <div className="hidden md:flex items-center justify-center">
            <div className="relative">
              <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Shield className="h-12 w-12 text-white/80" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center">
                <Target className="h-3 w-3 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          const TrendIcon = kpi.trendUp ? ArrowUpRight : ArrowDownRight;
          return (
            <div
              key={index}
              className={`${kpi.bgLight} rounded-2xl p-6 border border-white/50 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group`}
              data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-3">
                    {kpi.title}
                  </p>
                  <p className="text-4xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {kpi.value}{kpi.suffix || ''}
                  </p>
                  {kpi.trend ? (
                    <div className={`flex items-center gap-1.5 text-sm font-semibold ${kpi.trend.isUp ? 'text-emerald-600' : 'text-red-500'}`}>
                      <TrendIcon className="h-4 w-4" />
                      <span>{kpi.trend.value}</span>
                      <span className="text-[#94a3b8] font-normal text-xs">vs mês anterior</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-sm text-[#94a3b8]">
                      <span className="text-xs">Sem histórico anterior</span>
                    </div>
                  )}
                </div>
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${kpi.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="text-white" size={26} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compliance Status */}
      <div 
        className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(30,58,95,0.05)] border border-[rgba(30,58,95,0.05)]"
        data-testid="compliance-card"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
              stats?.compliance_status === 'conforme'
                ? 'bg-gradient-to-br from-emerald-500 to-green-400'
                : 'bg-gradient-to-br from-orange-500 to-amber-400'
            }`}>
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 
                className="text-lg font-bold text-[#1e3a5f] mb-1"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Status de Conformidade NR-1
              </h3>
              <p className="text-[#64748b] text-sm">
                {stats?.compliance_status === 'conforme'
                  ? 'Empresa em conformidade com as normas regulamentadoras'
                  : 'Atenção: Existem riscos críticos que precisam de ação'}
              </p>
            </div>
          </div>
          <div
            className={`px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-wider ${
              stats?.compliance_status === 'conforme'
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
            }`}
          >
            {stats?.compliance_status === 'conforme' ? 'Conforme' : 'Atenção Necessária'}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Training Widget */}
        <TrainingWidget />
        
        {/* Risk Distribution */}
        <div 
          className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(30,58,95,0.05)] border border-[rgba(30,58,95,0.05)]"
          data-testid="risk-distribution-chart"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <h3 
              className="text-lg font-bold text-[#1e3a5f]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Distribuição de Riscos
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={riskChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                outerRadius={85}
                innerRadius={40}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={2}
              >
                {riskChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid rgba(30,58,95,0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(30,58,95,0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {riskChartData.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-xs text-[#64748b]">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Risk by Type */}
        <div 
          className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(30,58,95,0.05)] border border-[rgba(30,58,95,0.05)]"
          data-testid="risk-by-type-chart"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0d9488] to-teal-400 flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <h3 
              className="text-lg font-bold text-[#1e3a5f]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Riscos por Tipo
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={riskTypeData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,58,95,0.08)" horizontal={false} />
              <XAxis 
                type="number"
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(30,58,95,0.1)' }}
              />
              <YAxis 
                type="category"
                dataKey="name" 
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(30,58,95,0.1)' }}
                width={90}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid rgba(30,58,95,0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(30,58,95,0.1)'
                }}
              />
              <Bar 
                dataKey="value" 
                fill="#0d9488"
                radius={[0, 6, 6, 0]}
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
